import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { XUXListContainerComponent } from '../list/xux-list.container.component';
import {
  IBaseEntityType,
  IDocumentOperationOptions,
  ENTITY_TYPES,
  ObjectId,
  ToolbarButtonDefinition,
  XuxGridAction,
  XUX_GRID_ACTIONS,
  IBaseEntityResponse,
  IXVIListColumn,
  IModalDialogResult,
  IKeyValueAny,
  XVIToolbarButtonDefinition,
  IUpdateDocumentsResult,
  IUpdateResultExpanded,
  IFormListControlMapperOptions,
  Primitive,
  FormListData,
} from '@shared/types';
import {
  AbstractControl,
  AbstractControlOptions,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import {
  BTN_DISCARD_CHANGES,
  BTN_SAVE_LIST,
} from '@text-content/button-templates';
import {
  filter,
  forEach,
  isEmpty,
  map,
  values,
  keys,
  isFunction,
  some,
  get,
  isEqual,
  merge,
  assign,
  pick,
  chain,
  set,
  isString,
  reduce,
  unset,
} from 'lodash';
import {
  cloneDataWithDepth,
  flatTreeDataByHasChildren,
  generateObjectId,
  generateId,
  isErrorsObject,
} from '@shared/utils';
import { Observable, rx } from '@shared/rx';
import { XuxGridActionPayload } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/grid-action.type';
import {
  getAllRows,
  isLeaf,
} from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { I18n } from '@app/i18n/strings-keys';
import {
  isRootRowNode,
  updateNestedNodesAfterFiltered,
  updateRowNodesAfterFiltered,
} from '@app/shared/helpers/ag-grid-helpers/ag-grid-helpers';
import { IAgGridNavigationOptions } from '@app/shared/common/ag-grid-navigation/keyboard-navigation.types';
import { INotification } from '@app/store/reducers/notifications/notifications.reducer';
import {
  IDatepickerConfig,
  IDatepickerValue,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/components/xux-datepicker/types';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-form-list',
  templateUrl: 'xux-form-list.container.html',
  styleUrls: [
    '../list/xux-list.container.scss',
    'xux-form-list.container.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class XUXFormListContainerComponent
  extends XUXListContainerComponent
  implements OnInit, OnDestroy
{
  public formListEntity$: Observable<IBaseEntityResponse>;
  public formListGroup: FormGroup;
  public formListControl: FormControl = new FormControl(null);
  // Entity type that will be sent to the server from the form-list
  public saveListDataEntity: ENTITY_TYPES;
  public mainGridButtons: XVIToolbarButtonDefinition[];
  public saveOperationId: ObjectId;
  public showForm: boolean = true;
  /**
   * is enable ag-grid keyboard navigation for the form-list grid, default value is true
   */
  public enableGridNavigate: boolean = true;

  /**
   * use this to track if the user has already confirmed once about leaving the page with unsaved changes, so we don't show him the confirmation dialog multiple times when he tries to leave the page without saving changes after confirming once, and also to reset it back to false when he decides to stay on the page after seeing the confirmation dialog
   */
  public isConfirmedOnce: boolean = false;

  public disabled: boolean;

  public navigationOptions: Partial<IAgGridNavigationOptions> = {
    alwaysFindNextEditCell: true,
  };

  public calendarConfig: IDatepickerConfig = null;
  public calendarValue: IDatepickerValue = null;
  public showCalendar: boolean = false;

  public get datepickerLabel(): string {
    const dateStart = this.calendarValue?.value?.dateStart;
    const dateEnd = this.calendarValue?.value?.dateEnd;

    if (!dateStart || !dateEnd) {
      return '';
    }

    const start = this.entityService.companyService.convertUTCDateWithTimeZone(
      dateStart as string,
      true,
    );
    const end = this.entityService.companyService.convertUTCDateWithTimeZone(
      dateEnd as string,
      true,
    );

    return `${start} - ${end}`;
  }

  public onDateChange(value: IDatepickerValue): void {
    const dateStart = value?.value?.dateStart || null;
    const dateEnd = value?.value?.dateEnd || dateStart;
    this.calendarValue = value
      ? { ...value, value: { dateStart, dateEnd } }
      : null;

    this.uiActions.updateListViewData({
      extraFilterModel: {
        dateRange: {
          dateStart,
          dateEnd,
          type: value?.type,
        },
      },
    });
  }

  public saveChangesBtn: XVIToolbarButtonDefinition = {
    ...BTN_SAVE_LIST,
    class: 'pull-left btn-primary',
    splitDropdown: true,
    click: this.saveChangesBtnClick.bind(this),
  };

  public ngOnInit(): void {
    super.ngOnInit();

    this.userGridOptions = assign({}, this.userGridOptions, {
      /**
       * default with true to avoid click to edit when the entity doesn't configure it,
       * but if the entity configures it explicitly to false, we will respect that
       */
      suppressClickEdit: get(
        this.entityService,
        'config.list.userGridOptions.suppressClickEdit',
        true,
      ),
    });

    // combine navigation options from entity config with default options
    this.navigationOptions = assign(
      {},
      this.navigationOptions,
      get(this.entityService, 'config.list.navigationOptions', {}),
      <IAgGridNavigationOptions>{
        // tree view mode options coming form entity config
        ...pick(
          get(this.entityService, ['config', 'list', 'gridOptions'], {}),
          ['treeViewMode', 'treeChildrenPropertyName'],
        ),
      },
    );

    this.setFormListGroup();
    this.setMainGridButtons();
    this.handleDisable();
    this.handleGridEntitySubscription();
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public handleDisable(): void {
    this.disabled = !this.canManage;
  }

  public updateParentAfterFiltered(): void {}

  public updateTreeDataAfterFilterChanged(): void {
    if (!this.isTreeViewMode) {
      return;
    }
  }

  /**
   * inlineCellEditComplete
   * will handle inline cell edit complete action
   *
   * @param payload
   */
  public inlineCellEditComplete(payload: XuxGridAction['payload']): boolean {
    if (this.gridOptions.treeViewMode) {
      return this.inlineEditTreeStructure(payload);
    } else {
      return this.inlineEditFlatStructure(payload);
    }
  }

  /**
   * inlineEditTreeStructure
   * will handle inline edit for tree structure
   *
   * @param payload
   * @returns is need to mark grid as dirty
   */
  public inlineEditTreeStructure(payload: XuxGridAction['payload']): boolean {
    const rowId = get(payload, 'rowId', null);
    const field = get(payload, 'field', null);
    const rowValue = get(payload, 'value', null);

    const rowNode = this.gridApi.getRowNode(rowId) ?? payload.rowNode;

    if (!rowNode) {
      return false;
    }

    return updateNestedNodesAfterFiltered(rowNode, {
      gridApi: this.gridApi,
      rowField: field,
      rowValue,
      shouldUpdate: get(this.entityService, [
        'config',
        'list',
        'shouldUpdateNodes',
      ]),
    });
  }

  /**
   * inlineEditFlatStructure
   * will handle inline edit for flat structure
   *
   * @param payload
   */
  public inlineEditFlatStructure(_payload: XuxGridAction['payload']): boolean {
    return true;
  }

  /**
   * Method for updating all parent nodes column data recursively
   *
   * @param params
   * @returns
   */
  public updateParentColData(params: {
    rowNode: RowNode;
    rowValue: Primitive;
    rowField: string;
    treeChildrenPropertyName: string;
    recursively?: boolean;
    refreshGrid?: boolean;
  }): void {
    const {
      rowNode,
      rowValue,
      rowField,
      treeChildrenPropertyName,
      recursively = true,
      refreshGrid = true,
    } = params;

    const parentNode = rowNode.parent;

    if (!parentNode || isRootRowNode(parentNode)) {
      return;
    }

    const children = parentNode.childrenAfterGroup;

    if (isEmpty(children)) {
      return;
    }

    const isSomeChildNotSame = some(children, (child) => {
      return (
        !isEqual(child, rowNode) &&
        !isEqual(get(child, ['data', rowField]), rowValue)
      );
    });

    const newValue = isSomeChildNotSame ? null : rowValue;

    // Set parent node sync field to false
    parentNode.setDataValue(rowField, newValue);

    if (refreshGrid) {
      this.gridApi.refreshCells({ rowNodes: [parentNode] });
    }

    // Update next parent node recursively
    if (recursively) {
      this.updateParentColData({
        rowField,
        treeChildrenPropertyName,
        recursively,
        rowNode: parentNode,
        rowValue: newValue,
      });
    }
  }

  /**
   * Method for updating all child nodes column data recursively
   *
   * @param params
   * @returns
   */
  public updateChildColData(params: {
    rowNode: RowNode;
    rowValue: Primitive;
    rowField: string;
    treeChildrenPropertyName: string;
    recursively?: boolean;
    refreshGrid?: boolean;
  }): void {
    const {
      rowNode,
      rowValue,
      rowField,
      treeChildrenPropertyName,
      recursively = true,
      refreshGrid = true,
    } = params;

    const children = rowNode.childrenAfterGroup;

    if (isEmpty(children)) {
      return;
    }

    forEach(children, (childNode) => {
      childNode.setDataValue(rowField, rowValue);

      // Update next child nodes recursively
      if (recursively) {
        this.updateChildColData({
          rowField,
          treeChildrenPropertyName,
          recursively,
          rowValue,
          rowNode: childNode,
          refreshGrid: false,
        });
      }
    });

    if (refreshGrid) {
      this.gridApi.refreshCells({ rowNodes: children });
    }
  }

  public async onGridAction(action: XuxGridAction): Promise<void> {
    await super.onGridAction(action);
    const payload = action.payload;
    const actionType = action.type;

    switch (actionType) {
      case XUX_GRID_ACTIONS.GRID_READY:
        const context = get(this.gridApi, [
          'gridOptionsWrapper',
          'gridOptions',
          'context',
        ]);

        if (context) {
          // update context with form control and form group to make them available in cell renderers and editors, so they can use it for different purposes like validation, value update, etc.
          set(context, 'formControl', this.formListControl);
          set(context, 'formGroup', this.formListGroup);
        }
        break;
      case XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED:
      case XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED:
        updateRowNodesAfterFiltered({
          gridApi: this.gridApi,
          gridOptions: this.gridOptions,
          field: this.gridOptions.syncTreeFieldAfterFilter,
        });

        break;
      case XUX_GRID_ACTIONS.ACTION_BUTTON_CLICKED:
        await this.handleActionButtonClicked(payload);
        break;
      case XUX_GRID_ACTIONS.ITEM_DATA_CHANGED:
        const updateSuccess = this.inlineCellEditComplete(payload);

        const topParentNode = this.getTopParentNode(
          this.getActionRowNode(payload),
        );

        const partialUpdateIdentifyPath: string = get(
          this.entityService,
          'config.list.partialUpdateIdentifyPath',
          null,
        );

        this.storeDataInControl([topParentNode?.data], {
          partialUpdate: isEmpty(partialUpdateIdentifyPath)
            ? true
            : { identifyPath: partialUpdateIdentifyPath },
          markDirty: updateSuccess,
        });
        break;
      case XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED:
        // only sync data without marking control as dirty
        const keepAllDataOnSave = get(
          this.entityService,
          'config.list.keepAllDataOnSave',
          false,
        );

        this.storeDataInControl(keepAllDataOnSave ? payload.rowData : [], {
          markDirty: false,
        });

        // should refresh tree after items array changed
        updateRowNodesAfterFiltered({
          gridApi: this.gridApi,
          gridOptions: this.gridOptions,
          field: this.gridOptions.syncTreeFieldAfterFilter,
        });
        break;
      default:
    }
  }

  public getActionRowNode(payload: XuxGridActionPayload): RowNode {
    return this.gridApi.getRowNode(payload.rowId) ?? payload.rowNode;
  }

  public getTopParentNode(rowNode: RowNode): RowNode {
    const topParentNode = rowNode?.parent;

    if (!topParentNode || isRootRowNode(topParentNode)) {
      return rowNode;
    }

    if (isEqual(topParentNode.level, 0) || isEqual(topParentNode.uiLevel, 0)) {
      return topParentNode;
    }

    return this.getTopParentNode(topParentNode);
  }

  /**
   * Method for handling grid action buttons clicks (including 3-dot menu button)
   */
  public async handleActionButtonClicked(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    payload: XuxGridActionPayload,
  ): Promise<void> {}

  public async connectToEntity(
    entityType: ENTITY_TYPES,
  ): Promise<ENTITY_TYPES> {
    await super.connectToEntity(entityType);

    // Init subscription on setting up the grid items and adding some mappers to this process
    this.handleGridItemsSubscription();

    return entityType;
  }

  /**
   * Method for handling subscription on items$ (grid items observable)
   * and customizing "formListControlToGridMapper" if it's needed
   */
  public handleGridItemsSubscription(): void {
    this.subscriptions.push(
      this.items$
        .pipe(
          rx.map((items) => {
            return this.entityService.formListControlToGridMapper(
              this.formListControl,
              {
                gridItems: items,
                dependentData: this.dependentEntitiesData,
              },
            );
          }),
        )
        .subscribe(),
    );
  }

  /**
   * Method handles the click event on the "Save Changes" button on the form-list and
   * sends bulk-update request to the server to save changed grid items
   */
  public async saveChangesBtnClick(
    btnValue: string,
    changedItems: FormListData = {},
    isUnsavedChanges: boolean = false,
  ): Promise<IUpdateResultExpanded> {
    // We need to generate a new saveOperationId for each save process
    // to use it as a cart entity_id
    this.saveOperationId = generateObjectId();
    if (!this.formListGroup.dirty && !isUnsavedChanges) return;

    const requestOptions: IDocumentOperationOptions = this.getRequestOptions();

    const result = await this.updateDocuments(
      this.saveListDataEntity,
      requestOptions,
      changedItems,
    );

    if (!isUnsavedChanges) await this.resetStateAfterSave();

    return result;
  }

  /**
   * Method for updating list of not bulk documents
   *
   * @param entityType - Type of an entity
   * @param options - Request options
   * @returns
   */
  public async updateDocuments(
    entityType: ENTITY_TYPES,
    options: IDocumentOperationOptions = {},
    changedItems: FormListData,
  ): Promise<IUpdateDocumentsResult> {
    const documentsForUpdate =
      await this.prepareDocumentsFromListGridData(changedItems);

    if (
      isErrorsObject(documentsForUpdate) ||
      some(documentsForUpdate, isErrorsObject)
    ) {
      return documentsForUpdate;
    }

    if (isEmpty(documentsForUpdate)) return [];

    return this.updateEntities(
      documentsForUpdate,
      this.saveListDataEntity ?? this.entityType,
      {
        ...options,
        transactionId: this.saveOperationId,
      },
    );
  }

  /**
   * Method for updating list of documents using the "updateEntities"
   * endpoint
   *
   * @param documents - List of documents for updating
   * @param entityType - Type of an entity
   * @param options - Request options
   * @returns
   */
  public async updateEntities(
    documents: IUpdateDocumentsResult,
    entityType: ENTITY_TYPES,
    options: IDocumentOperationOptions = {},
  ): Promise<any> {
    return this.apiActions.updateEntityAsPromised(
      entityType,
      documents,
      options,
    );
  }

  /**
   * Method is used for asking a user about saving changed data in IndexedDB
   * when he tries to leave the page
   */
  public async canDeactivate(): Promise<boolean> {
    // We don't ask a user about saving changed data in IndexedDB
    // if the formListGroup isn't dirty or if a user hit the Save Changes button
    // will return true in this case to allow deactivation without showing the confirmation dialog
    if (!this.formListGroup.dirty || this.isConfirmedOnce) return true;

    const options: IKeyValueAny = this.getModalOptionEntitiesConfig(
      this.entityType,
    );
    const { confirmed }: IModalDialogResult =
      await this.uiActions.confirm(options);

    if (confirmed) {
      this.isConfirmedOnce = true;
      this.resetFormListGrid();
    } else {
      this.isConfirmedOnce = false;
    }

    return confirmed;
  }

  public getModalOptionEntitiesConfig(_entityType: ENTITY_TYPES): IKeyValueAny {
    return {
      text: this.i18nService.translate(
        I18n.ConfirmMessages.LeaveWithUnsavedChanges,
      ),
    };
  }

  /**
   * Method reset the form-list control, clear the IndexedDB
   * and refresh the form-list grid
   */
  public async resetToInitialState(): Promise<void> {
    /**
     * First, we have to reset the form-list control to avoid putting changed data
     * from the control to the grid. After resetting the control we refresh the grid
     * to fetch updated data (REFRESH GRID STEP #2)
     */
    this.resetControls();
    this.refresh();
  }

  /**
   * The handler for saving data into formListControl
   *
   * @param listData - data from the form-list grid
   * @param options - mapper options
   */
  public async storeDataInControl(
    listData: IBaseEntityType[],
    options: IFormListControlMapperOptions = {},
  ): Promise<void> {
    const defaultOptions: IFormListControlMapperOptions = {
      markDirty: true,
    };

    const storeOptions = merge({}, defaultOptions, options);

    const preparedDataForSave = this.entityService.gridToFormListControlMapper(
      listData,
      this.formListControl,
      storeOptions,
    ) as FormListData;

    if (isEmpty(preparedDataForSave)) {
      return;
    }

    this.setControlValue(preparedDataForSave, storeOptions);
  }

  /**
   * Method discards all the changes that were done on the grid and
   * clears the form-list control value and removed saved in IndexedDB data
   */
  public discardAllChanges(isResetAllEditableColumns: boolean = true): void {
    this.resetControls();
    this.resetFormListGrid(isResetAllEditableColumns);
  }

  /**
   * Method for setting value to the main form-list control
   *
   * @param value - new control value
   */
  public setControlValue(
    value: FormListData = null,
    options?: {
      markDirty?: boolean;
    },
  ): void {
    const { markDirty = true } = options || {};

    this.formListControl.setValue(value);

    if (!markDirty) {
      return;
    }

    this.formListControl.markAsDirty();
    this.formListControl.markAsTouched();
  }

  /**
   * Method for setting value to some form-list control
   *
   * @param formListControl - form control
   * @param value - new control value
   */
  public setFormListControlValue(
    formListControl: AbstractControl,
    value: FormListData = null,
  ): void {
    if (!formListControl) return;

    formListControl.setValue(value);
    formListControl.markAsDirty();
    formListControl.markAsTouched();
  }

  public getFormListControlByName(formControlName: string): AbstractControl {
    return this.formListGroup.get(formControlName);
  }

  protected get formValidators():
    | ValidatorFn
    | ValidatorFn[]
    | AbstractControlOptions {
    return [];
  }

  public setFormListGroup(): void {
    const formValidators = this.formValidators;

    this.formListGroup = new FormGroup(
      this.setFormListControls(),
      formValidators,
    );
    this.setFormListControl();

    this.addFormValueChangesSubscription(formValidators);
  }

  public addFormValueChangesSubscription(
    formValidators: ValidatorFn | ValidatorFn[] | AbstractControlOptions,
  ): void {
    /**
     * cache object for error messages to avoid showing multiple toasters with the same message when the form value changes and has some validation errors,
     * and also to remove the message when the error is fixed after value changes
     */
    let allMessages: Record<
      string,
      { notificationId: string; message: string }
    > = {};

    this.subscriptions.push(
      this.formListGroup.valueChanges
        .pipe(rx.distinctUntilChanged())
        .subscribe(() => {
          // if there is no validators we don't need to redraw rows and show error message
          if (isEmpty(formValidators)) {
            return;
          }

          this.gridApi.redrawRows({
            rowNodes: getAllRows(this.gridApi, {
              selector: (node) => !isLeaf(node) && node.allChildrenCount > 0,
              valueSelector: (node) => node,
              withFilterAndSort: true,
            }),
          });

          if (isEmpty(this.formListGroup.errors)) {
            //clear all error messages if there is no errors after value changes
            forEach(allMessages, (messageValue) => {
              this.uiActions.removeNotification(messageValue.notificationId);
            });

            allMessages = {};

            return;
          }

          const messages: Record<
            string,
            { notificationId: string; message: string }
          > = reduce(
            this.formListGroup.errors,
            (accAllMessages, error, key) => {
              if (!isEmpty(get(allMessages, key))) {
                return accAllMessages;
              }

              const message = {
                notificationId: generateId(),
                message: isString(error) ? error : get(error, 'message', null),
              };

              // update cache
              set(allMessages, key, message);

              return {
                ...accAllMessages,
                [key]: message,
              };
            },
            {},
          );

          forEach(messages, (messageValue, messageKey) => {
            this.uiActions.showErrorToasterMessage(<Partial<INotification>>{
              id: messageValue.notificationId,
              errorLabel: I18n.Common.Error,
              title: messageValue.message,
              notificationBoxOptions: {
                closeNotification: () => {
                  this.uiActions.removeNotification(
                    messageValue.notificationId,
                  );

                  unset(allMessages, messageKey);
                },
              },
            });
          });
        }),
    );
  }

  protected setFormListControl(): void {
    this.formListControl = this.formListGroup.get(
      this.entityType,
    ) as FormControl;
  }

  protected setFormListControls(): { [key: string]: FormControl } {
    return {
      [this.entityType]: new FormControl(null),
    };
  }

  /**
   * u can configure which fields will be saved from tree nodes
   *
   * @returns array of fields names
   */
  protected getSaveSelectFields(): string[] {
    return [];
  }

  protected beforeSaveMapper(items: IBaseEntityType[]): IUpdateDocumentsResult {
    const beforeSaveMapper = get(
      this.entityService,
      ['config', 'list', 'beforeSaveMapper'],
      () => items,
    );

    if (isFunction(beforeSaveMapper)) {
      return beforeSaveMapper(items);
    }

    return items;
  }

  /**
   * Method for preparing data from the form-list control before saving it on the server
   *
   * @param data - object data from the form-list control
   * @returns
   */
  protected async prepareFormListDataBeforeSave(
    data: FormListData,
  ): Promise<IUpdateDocumentsResult> {
    const selectedFields = this.getSaveSelectFields();

    const dataValues = values(data);

    let partialDataValues = isEmpty(selectedFields)
      ? dataValues
      : chain(dataValues)
          .map((value) => pick(value, selectedFields))
          .value();

    if (
      this.gridOptions.treeViewMode &&
      !this.gridOptions.keepTreeStructureWhenSave
    ) {
      // use origin dataValues to flatten tree
      partialDataValues = flatTreeDataByHasChildren(dataValues, {
        selectFields: selectedFields,
      });
    }

    return Promise.resolve(this.beforeSaveMapper(partialDataValues));
  }

  protected updateActionButtonsStatus(): void {
    super.updateActionButtonsStatus();
    const buttons: ToolbarButtonDefinition[] =
      this.entityService?.config?.list?.inlineEditButtons ?? [];
    this.rowActionButtons = this.i18nService.translateButtonsText(
      this.setButtonsBehavior(buttons),
    );
  }

  protected setButtonsBehavior(
    buttons: ToolbarButtonDefinition[],
  ): ToolbarButtonDefinition[] {
    return buttons
      .map((btn) => ({
        ...btn,
        caption: isFunction(btn.caption)
          ? btn.caption.call(this, this.selectedItems)
          : btn.caption,
        disabled: isFunction(btn.disabled)
          ? btn.disabled.call(this, this.selectedItems)
          : btn.disabled,
        hidden: isFunction(btn.hidden)
          ? btn.hidden.call(this, this.selectedItems)
          : btn.hidden,
        class: '',
      }))
      .filter((btn) => !btn.hidden);
  }

  protected getRequestOptions(): IDocumentOperationOptions {
    return {};
  }

  protected async prepareDocumentsFromListGridData(
    changedItems: FormListData = {},
  ): Promise<IUpdateDocumentsResult> {
    const listGridData: FormListData = !isEmpty(changedItems)
      ? changedItems
      : cloneDataWithDepth(this.formListControl.value, 2) ?? {};
    if (isEmpty(listGridData)) return [];

    const preparedDataForSave: IUpdateDocumentsResult =
      await this.prepareFormListDataBeforeSave(listGridData);

    return preparedDataForSave;
  }

  protected setMainGridButtons(): void {
    this.mainGridButtons = [
      { ...this.saveChangesBtn, childItems: [...this.getChildGridButtons()] },
    ];
  }

  protected getChildGridButtons(): ToolbarButtonDefinition[] {
    return [
      {
        ...BTN_DISCARD_CHANGES,
        click: this.discardAllChanges.bind(this, true),
        disabledObs: this.getFormGroupValueChangesObs.bind(this)(),
      },
    ];
  }

  public getFormGroupValueChangesObs(): Observable<boolean> {
    return this.formListGroup.valueChanges.pipe(
      rx.startWith(null),
      rx.debounceTime(0),
      rx.map(() => !this.formListGroup.dirty),
    );
  }

  public async resetStateAfterSave(): Promise<void> {
    await this.resetToInitialState();
  }

  /**
   * Method runs on the component initialisation, checks data in IndexedDB
   * and sets value to formListControl
   */
  protected async initFormListData(): Promise<void> {
    this.setControlValue();
  }

  protected handleGridEntitySubscription(): void {
    const entityType: ENTITY_TYPES =
      this.entityService?.config?.ENTITY_TYPE ?? null;
    if (!entityType) return;

    this.formListEntity$ = this.stateService.select(['entities', entityType]);
    this.subscriptions.push(
      this.formListEntity$.subscribe((data) => {
        if (!data.total || !isEmpty(this.formListControl.value)) return;
        /**
         * This subscription on the form-list entity observable helps
         * to update the list grid after saving (REFRESH GRID STEP #1)
         */
        this.resetFormListGrid();
      }),
    );
  }

  public resetFormListGrid(isResetAllEditableColumns: boolean = true): void {
    if (!this.gridApi || !isResetAllEditableColumns) return;

    const listColumns: IXVIListColumn[] =
      this.entityService?.config?.list?.columns ?? [];
    const editableColumns: IXVIListColumn[] = filter(
      listColumns,
      ({ editable }) => editable === true || isFunction(editable),
    );
    if (isEmpty(editableColumns)) return;

    const editableColumnsPaths: string[] = map(editableColumns, 'path');
    const rowData: IBaseEntityType[] = getAllRows(this.gridApi);
    const update = map(rowData, (listGridItem) => {
      forEach([...editableColumnsPaths], (path) => {
        delete listGridItem[path];
      });
      delete listGridItem.virtual;

      return listGridItem;
    });
    this.gridApi.updateRowData({ update });
  }

  public resetControls(value: FormListData = null): void {
    keys(this.formListGroup.controls).forEach((key) => {
      const control = this.formListGroup.get(key);
      control.setValue(value);
      control.markAsPristine();
      control.markAsUntouched();
    });
  }
}
